import { HUBSPOT_SUBJECTS } from '@vangst/services/hubspot'
import { CompanyComputed } from '@vangst/services/oogst/company/useCompany'
import { MemberComputed } from '@vangst/services/oogst/member/useMember'
import {
  MemberFragment,
  Viewer_UserFragment,
} from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { useState } from 'react'
import ClickyBtn from '../../components/navigation/ClickyBtn'
import ClickyLink from '../../components/navigation/ClickyLink'
import Avatar from '../../components/views/Avatar'
import DialogContact from '../job/feedback/DialogContact'

type UserScope = {
  isBreathing?: boolean
  isCompany?: boolean
  isInternalUser?: boolean
  isMember?: boolean
}

export type UserFrags = Viewer_UserFragment | MemberComputed | CompanyComputed
export default function DashboardHeader({
  userScope,
}: {
  userScope: UserScope
}) {
  const { viewer } = useViewer()
  const { isInternalUser, isCompany, isMember } = userScope
  const [showServicesModal, setShowServicesModal] = useState(false)

  const detailUrl =
    viewer?.client?.routes?.detail || viewer?.routes?.detail || '#'
  const avatarUrl = viewer?.client?.avatarUrl || viewer?.avatarUrl || undefined
  const initials = viewer?.client?.initials || viewer?.initials || '...'
  const name = viewer?.client?.name || viewer?.fullName || '...'
  const nameOrId = viewer?.nameOrId || '...'

  return (
    <header className="contain-xyc flow-y-sm min-h-20">
      <h1 className="heading-md">
        <ClickyLink href={detailUrl}>
          <span className="flow-x-xs items-center">
            <Avatar
              className="mood-blue-dark size-20 shrink-0"
              alt={initials}
              src={avatarUrl}
              role="presentation"
              width={80}
              height={80}
            />
            <span className="ml-4 flex flex-col">
              <span>{name}</span>
              <span className="text-sm">@{nameOrId}</span>
            </span>
          </span>
        </ClickyLink>
      </h1>

      <span className="flow-y-xs justify-center sm:flow-x-sm sm:ml-auto">
        {isCompany && (
          <CompanyCTAs
            action={setShowServicesModal}
            href={viewer?.client?.routes?.jobsNew ?? ''}
          />
        )}
        {isInternalUser && <InternalUserCTAs />}
        {isMember && !isInternalUser && (
          <MemberCTAs
            action={(viewer as MemberComputed)?.indispensableSurveyLink}
          />
        )}
      </span>
      <Dialogs
        setShowServicesModal={setShowServicesModal}
        showServicesModal={showServicesModal}
        viewer={viewer as MemberFragment}
      />
    </header>
  )
}

export function CompanyCTAs({
  action,
  href,
}: {
  action: (state: boolean) => void
  href: string
}) {
  return (
    <>
      <ClickyBtn href={href} className="mood-orange mr-4 min-w-28">
        Post A Job
      </ClickyBtn>
      <ClickyBtn onClick={() => action(true)} className="mood-orange mr-4">
        Contact Customer Success
      </ClickyBtn>
    </>
  )
}

export function InternalUserCTAs() {
  return (
    <>
      <ClickyBtn
        className="mood-orange mt-4 md:ml-auto md:mt-0"
        href={Routes.INVITE_USER || ''}
        rel="noopener noreferrer"
        target="_blank"
      >
        Invite User
      </ClickyBtn>
      <ClickyBtn
        className="mood-orange mt-4 md:ml-auto md:mt-0"
        href={Routes.COMPANIES_JOBS_NEW.replace(
          ':displayname',
          'vangst-recruiting' || '',
        )}
        rel="noopener noreferrer"
      >
        Post a Job
      </ClickyBtn>
    </>
  )
}

export function MemberCTAs({ action }: { action?: string }) {
  if (action == null) return null
  return (
    <ClickyBtn
      className="mood-orange mt-4 md:ml-auto md:mt-0"
      href={action}
      rel="noopener noreferrer"
      target="_blank"
    >
      Give Us Your Feedback
    </ClickyBtn>
  )
}

export function Dialogs({
  setShowServicesModal,
  showServicesModal,
  viewer,
}: {
  setShowServicesModal: (state: boolean) => void
  showServicesModal: boolean
  viewer: MemberFragment
}) {
  return (
    <>
      {showServicesModal && (
        <DialogContact
          email={viewer?.email ?? ''}
          phone={viewer?.phoneNumber ?? ''}
          state={viewer?.location?.state ?? ''}
          firstName={viewer?.firstName ?? ''}
          lastName={viewer?.lastName ?? ''}
          handleCancel={() => setShowServicesModal(false)}
          hideDescription={true}
          title="Contact Customer Success"
          hubspotContactSubject={HUBSPOT_SUBJECTS.COMPANY_SUPPORT}
        />
      )}
    </>
  )
}
